import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, MaskedText } from "@/components/motion/Reveal";
import { ArrowUpLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOOKING_FOR = ["Everyday", "A wedding or event", "Evening", "Bridal", "Just exploring"];
const PIECES = ["Dresses", "Knits & Sweatshirts", "Shrugs & Layers", "Not sure yet"];
const FITTING_TYPE = ["In person", "Video call", "Either"];

const inputCls =
  "w-full border-b border-ink/25 bg-transparent pb-3 pt-1 font-display text-lg font-extralight tracking-tight text-ink outline-none transition-colors duration-300 placeholder:text-muted/80 focus:border-ink";

function Pills({ label, values, selected, onSelect, testid }) {
  return (
    <div data-testid={testid}>
      <span className="overline mb-3 block text-muted">{label}</span>
      <div className="flex flex-wrap gap-2.5">
        {values.map((v) => {
          const active = selected === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => onSelect(v)}
              data-testid={`${testid}-${v.replace(/\s+/g, "-").toLowerCase()}`}
              className={`border px-4 py-2 font-body text-sm transition-colors duration-300 ${
                active ? "border-ink bg-ink text-paper" : "border-ink/25 text-stone hover:border-ink"
              }`}
            >
              {v}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function KingsleyFitting() {
  const [f, setF] = useState({
    name: "",
    email: "",
    lookingFor: "",
    pieces: "",
    colours: "",
    size: "",
    timeline: "",
    fittingType: "",
    date: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const upd = (k) => (e) => set(k, e.target.value);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const message = [
        "FITTING REQUEST",
        `Looking for: ${f.lookingFor || "—"}`,
        `Drawn to: ${f.pieces || "—"}`,
        `Colours / fabrics: ${f.colours || "—"}`,
        `Usual size: ${f.size || "—"}`,
        `Needed by: ${f.timeline || "—"}`,
        `Fitting: ${f.fittingType || "—"}`,
        `Preferred date/time: ${f.date || "—"}`,
        "",
        f.notes || "(no note)",
      ].join("\n");
      await axios.post(`${API}/contact`, {
        name: f.name,
        email: f.email,
        projectType: "Fitting",
        message,
        section: "Kingsley",
      });
      toast.success("Thank you — your fitting request is in. I’ll be in touch to arrange it.");
      setF({ name: "", email: "", lookingFor: "", pieces: "", colours: "", size: "", timeline: "", fittingType: "", date: "", notes: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#EDECE9] px-6 pb-28 pt-32 md:px-12 md:pt-40">
      <div className="mx-auto max-w-[1100px]">
        <Reveal>
          <Link to="/kingsley" data-testid="fitting-back" className="group inline-flex items-center gap-2 text-stone transition-colors hover:text-ink">
            <ArrowUpLeft className="h-4 w-4 transition-transform duration-500 group-hover:-translate-x-0.5" />
            <span className="overline">The boutique</span>
          </Link>
        </Reveal>

        <div className="mt-10">
          <Reveal>
            <p className="overline text-muted">(Book a fitting)</p>
          </Reveal>
          <MaskedText
            as="h1"
            lines={["The dress you want —", "made for you."]}
            className="mt-5 font-display text-4xl font-extralight leading-[1.02] tracking-tight text-ink md:text-6xl"
          />
          <Reveal delay={0.25}>
            <p className="mt-8 max-w-xl font-body text-sm leading-relaxed text-stone md:text-base">
              Tell me a little about what you’re after and I’ll set up a fitting — in person or
              over video. A few quick questions to get us started.
            </p>
          </Reveal>
        </div>

        <form onSubmit={submit} data-testid="kingsley-fitting-form" className="mt-14 flex flex-col gap-12">
          <div className="grid gap-10 md:grid-cols-2">
            <label className="block">
              <span className="overline mb-3 block text-muted">Your Name *</span>
              <input data-testid="fitting-name" value={f.name} onChange={upd("name")} placeholder="Jane Doe" className={inputCls} />
            </label>
            <label className="block">
              <span className="overline mb-3 block text-muted">Email *</span>
              <input data-testid="fitting-email" type="email" value={f.email} onChange={upd("email")} placeholder="you@email.com" className={inputCls} />
            </label>
          </div>

          <Pills label="What are you looking for?" values={LOOKING_FOR} selected={f.lookingFor} onSelect={(v) => set("lookingFor", v)} testid="fitting-looking" />
          <Pills label="Which part of the line draws you?" values={PIECES} selected={f.pieces} onSelect={(v) => set("pieces", v)} testid="fitting-pieces" />

          <div className="grid gap-10 md:grid-cols-2">
            <label className="block">
              <span className="overline mb-3 block text-muted">Colours or fabrics you love</span>
              <input data-testid="fitting-colours" value={f.colours} onChange={upd("colours")} placeholder="Crimson, silk jersey…" className={inputCls} />
            </label>
            <label className="block">
              <span className="overline mb-3 block text-muted">Your usual size</span>
              <input data-testid="fitting-size" value={f.size} onChange={upd("size")} placeholder="e.g. US 6 / M" className={inputCls} />
            </label>
          </div>

          <div className="grid gap-10 md:grid-cols-2">
            <label className="block">
              <span className="overline mb-3 block text-muted">When do you need it?</span>
              <input data-testid="fitting-timeline" value={f.timeline} onChange={upd("timeline")} placeholder="A date, event or just browsing" className={inputCls} />
            </label>
            <label className="block">
              <span className="overline mb-3 block text-muted">Preferred date / time</span>
              <input data-testid="fitting-date" value={f.date} onChange={upd("date")} placeholder="Sat 12 July, afternoon" className={inputCls} />
            </label>
          </div>

          <Pills label="How would you like to meet?" values={FITTING_TYPE} selected={f.fittingType} onSelect={(v) => set("fittingType", v)} testid="fitting-type" />

          <label className="block">
            <span className="overline mb-3 block text-muted">Anything else?</span>
            <textarea data-testid="fitting-notes" value={f.notes} onChange={upd("notes")} rows={3} placeholder="Inspiration, a piece you saw, questions…" className={`${inputCls} resize-none`} />
          </label>

          <button
            type="submit"
            data-testid="fitting-submit"
            disabled={submitting}
            className="group inline-flex w-fit items-center gap-3 border border-ink px-10 py-4 transition-colors duration-500 hover:bg-ink hover:text-paper disabled:opacity-50"
          >
            <span className="overline">{submitting ? "Sending…" : "Request my fitting"}</span>
            <span className="transition-transform duration-500 group-hover:translate-x-1.5">↗</span>
          </button>
          <p className="font-body text-xs leading-relaxed text-muted">
            No payment taken here — we’ll talk through the piece, the details and timing together.
          </p>
        </form>
      </div>
    </div>
  );
}
